<template>
  <div class="flex lory-slider">
    <a
      :href="room.room_url"
      target="_blank"
      class="flex flex-nowrap overflow-x-scroll no-scrollbar rounded overflow-hidden will-change-scroll"
      :class="isAnimated ? '' : 'snap-mandatory snap-x'"
      @click="$emit('carouselClicked')"
      @mouseover.once="onFirstMouseOver"
      @touchstart.once="onFirstMouseOver"
      @scroll="onScroll"
      ref="scrollContainer"
    >
      <div
        v-for="(photo, photoIndex) in images"
        :key="room.room_url + photoIndex"
        class="snap-always snap-center min-w-full"
        ref="slide"
      >
        <img
          class="img w-full h-full block object-contain aspect-3/2"
          :src="(index < 3 && photoIndex === 0) || photo.loaded ? photo.url : null"
          :data-src="photo.url"
          :data-index="photoIndex"
          :alt="room.room_name"
          decoding="async"
        >
      </div>
    </a>
    <span class="arrow lory-prev" @click.stop="onArrowClick(-1)" />
    <span class="arrow lory-next" @click.stop="onArrowClick(1)" />
  </div>
</template>

<script>
import { useIntersectionObserver } from '@vueuse/core';
import throttle from 'lodash/throttle';

import { scrollToX } from '@/services/helpers';

export default {
  name: 'RoomNativeCarousel',
  emits: ['carouselClicked', 'beforeSlide'],

  props: {
    room: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    isAnimated: false,
    images: [],
    maxImages: 0,
    currentImageIndex: 0,
    expectedImageIndex: 0,
    currentAnimation: null,
  }),
  computed: {
    slideWidth() {
      return this.$refs.slide[0].getBoundingClientRect().width;
    },
  },
  watch: {
    expectedImageIndex() {
      this.loadImage(this.expectedImageIndex);

      setTimeout(() => {
        this.loadImage(this.expectedImageIndex - 1);
        this.loadImage(this.expectedImageIndex + 1);
      }, 250);
    },
  },
  methods: {
    onArrowClick(direction) {
      if (this.currentAnimation) {
        this.currentAnimation.stop();
      }

      const expectedImageIndex = this.expectedImageIndex + direction;
      if (expectedImageIndex < 0 || expectedImageIndex >= this.maxImages) {
        this.expectedImageIndex = expectedImageIndex < 0 ? this.maxImages - 1 : 0;

        this.$refs.scrollContainer.scrollLeft = this.slideWidth * this.expectedImageIndex;
        return;
      }

      this.expectedImageIndex = expectedImageIndex;
      this.scrollToImageAnimate(expectedImageIndex);
    },
    scrollToImageAnimate(imageIndex) {
      this.isAnimated = true;
      const px = this.slideWidth * imageIndex;
      this.currentAnimation = scrollToX(this.$refs.scrollContainer, px, 2000, 'easeInOutSine', () => {
        this.isAnimated = false;
        this.currentAnimation = null;
      });
      this.currentAnimation.start();
    },
    onFirstMouseOver() {
      this.loadImage(1);
      this.loadImage(this.maxImages - 1); // in the case of prev arrow
    },
    loadImage(index) {
      if (index < 0) {
        index = this.maxImages - 1;
      }
      if (index >= this.maxImages) {
        index = 0;
      }
      if (index >= 0 && this.images[index]) {
        this.images[index].loaded = true;
      }
    },
    onScroll: throttle(function () {
      const scrollContainer = this.$refs.scrollContainer;
      const scrollLeft = scrollContainer.scrollLeft;

      const currentImageIndex = scrollLeft / this.slideWidth;

      const i = Math.round(currentImageIndex);
      this.loadImage(i + 1);
      this.loadImage(i - 1);

      if (currentImageIndex === this.currentImageIndex || currentImageIndex !== Math.round(currentImageIndex)) {
        return;
      }

      // scrolling direction
      let arrowDirection = 'left';
      if (currentImageIndex > this.currentImageIndex) {
        arrowDirection = 'right';
      }

      // image indexes
      const previousImageIndex = this.currentImageIndex;
      this.currentImageIndex = currentImageIndex;
      this.expectedImageIndex = currentImageIndex;

      this.loadImage(currentImageIndex + 1);
      this.loadImage(currentImageIndex - 1);

      this.$emit('beforeSlide', {
        arrowDirection,
        photoUrlAfter: this.room.photos[currentImageIndex],
        photoIndex: previousImageIndex + 1, // actually it's not index, but number of photo
        currentPhotoNumber: currentImageIndex + 1,
        photoUrl: this.room.photos[currentImageIndex],
      });
    }, 50),
    initLazyLoadingFirstImage() {
      const intersectionObserver = useIntersectionObserver(this.$refs.slide[0], ([entry]) => {
        if (entry.isIntersecting) {
          this.loadImage(0);
          // preload next image with some delay to not compete with first
          setTimeout(() => {
            this.loadImage(1);
          }, 250);
          intersectionObserver.stop();
        }
      }, {
        rootMargin: '150px',
        threshold: 0.01,
      });
    },
  },
  beforeMount() {
    this.maxImages = this.room.photos.length;

    this.images = this.room.photos.map((url) => ({
      url,
      loaded: false,
    }));
  },
  mounted() {
    this.$nextTick(() => {
      this.initLazyLoadingFirstImage();
    });
  },
};
</script>

<style scoped>
.arrow {
  touch-action: pan-x pan-y;
  /* prevent double tap zoom on iOS */
}
</style>
